import { prefersReducedMotion } from '../accessibility.js';

/**
 * Camera / view system for the book scene.
 *
 * Owns the single requestAnimationFrame loop of the scene and publishes its
 * state as CSS custom properties on the scene root. Nothing else in the scene
 * is allowed to run its own animation loop.
 *
 * There is deliberately no idle motion and no overshoot: the loop stops once
 * the camera settles.
 *
 * IMPORTANT INVARIANT
 * In the interactive `live` phase the camera must stay axis-aligned: only
 * scale and translate. `pointer-bridge` inverts the camera transform through a
 * bounding-box ratio, which is exact for scale+translate. A rotateZ, or any
 * visible tilt, would desynchronise page dragging. Perspective and tilt
 * therefore live only in the `intro` phase, where pointer input is disabled.
 */

const INTRO = {
  desktop: { duration: 1600, tilt: 41, yaw: -14, scale: 0.88, persp: 1500 },
  tablet: { duration: 1200, tilt: 34, yaw: -11, scale: 0.91, persp: 1700 },
  phone: { duration: 1000, tilt: 26, yaw: -8, scale: 0.94, persp: 1900 }
};

/**
 * No idle drift and no overshoot anywhere in this camera: paper does not
 * breathe on its own, and a spring reads as UI motion rather than as a lens.
 * Zoom is exponential smoothing towards the target, which settles without
 * ever passing it.
 */
const ZOOM = { min: 0.8, max: 1.9, smoothing: 9 };

const OFFSET_LERP = 7;
const DOF_DECAY = 6;
const MAX_FRAME_SECONDS = 1 / 30;
const MAX_INTRO_STEP_SECONDS = 0.25;
const EPSILON = 0.0005;

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function easeOutCubic(t) {
  return 1 - (1 - t) ** 3;
}

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

/**
 * @param {object} options
 * @param {HTMLElement} options.root - element carrying the CSS custom properties
 * @param {'desktop'|'tablet'|'phone'} options.tier
 * @param {boolean} [options.skipIntro] - start settled; the fly-in belongs to
 *   the first appearance only, not to every scene rebuild (opening the cover
 *   rebuilds the scene and must not replay it)
 * @param {() => void} [options.onIntroEnd]
 */
export function createCamera({ root, tier = 'desktop', skipIntro = false, onIntroEnd }) {
  const reducedMotion = prefersReducedMotion();

  let currentTier = tier;
  let intro = INTRO[currentTier];

  let phase = reducedMotion || skipIntro ? 'live' : 'intro';
  let introElapsed = phase === 'live' ? intro.duration : 0;

  let zoom = 1;
  let zoomTarget = 1;

  let offsetX = 0;
  let offsetXTarget = 0;

  let dofPulse = 0;
  let zoomRate = 0;

  let frame = null;
  let lastTime = 0;
  let destroyed = false;

  function write(name, value) {
    root.style.setProperty(name, value);
  }

  function publish() {
    const introProgress = phase === 'intro' ? introElapsed / intro.duration : 1;
    const eased = easeOutCubic(clamp(introProgress, 0, 1));
    const easedTilt = easeInOutCubic(clamp(introProgress, 0, 1));
    const remaining = 1 - eased;

    const introScale = intro.scale + (1 - intro.scale) * eased;

    // Shallower depth of field the closer the camera pushes in, plus a short
    // focus-pull burst proportional to how fast the zoom is currently moving.
    const dofBase = clamp((zoom - 1) * 2.4, 0, 5);
    const dof = dofBase + dofPulse + remaining * 6;

    const scale = zoom * introScale;

    write('--cam-scale', String(scale));
    // The framing offset is expressed in the book's own unscaled pixels, but
    // translate() is applied in the parent's space, so it has to be scaled.
    write('--cam-x', `${offsetX * scale}px`);
    // Only a slight rise: the book must read as resting on the surface, not
    // hovering above it.
    write('--cam-y', `${-remaining * 8}px`);
    write('--cam-tilt', `${intro.tilt * (1 - easedTilt)}deg`);
    write('--cam-yaw', `${intro.yaw * (1 - easedTilt)}deg`);
    write('--cam-persp', `${intro.persp}px`);
    write('--cam-dof', `${dof.toFixed(2)}px`);
    // Unitless twin of --cam-dof so CSS can drive opacities from the same value.
    write('--cam-focus', dof.toFixed(3));
    write('--cam-lift', remaining.toFixed(3));
  }

  function isSettled() {
    // With no idle motion the loop is allowed to stop entirely, which is the
    // point: nothing should be animating while the reader is just reading.
    return (
      phase === 'live' &&
      Math.abs(zoom - zoomTarget) < EPSILON &&
      Math.abs(offsetX - offsetXTarget) < EPSILON &&
      dofPulse < EPSILON
    );
  }

  function step(time) {
    if (destroyed) return;

    const realDt = (time - lastTime) / 1000 || 0;
    // Physics is stepped with a clamped delta so a stall cannot explode the
    // spring, but the intro is timed against the wall clock: a slow first few
    // frames must not stretch a 1.6s camera move into three seconds.
    const dt = Math.min(MAX_FRAME_SECONDS, realDt);
    lastTime = time;

    if (phase === 'intro') {
      introElapsed += Math.min(MAX_INTRO_STEP_SECONDS, realDt) * 1000;
      if (introElapsed >= intro.duration) {
        introElapsed = intro.duration;
        phase = 'live';
        onIntroEnd?.();
      }
    }

    const previousZoom = zoom;

    if (reducedMotion) {
      zoom = zoomTarget;
      offsetX = offsetXTarget;
    } else {
      zoom += (zoomTarget - zoom) * Math.min(1, ZOOM.smoothing * dt);
      offsetX += (offsetXTarget - offsetX) * Math.min(1, OFFSET_LERP * dt);
    }

    zoomRate = dt > 0 ? Math.abs(zoom - previousZoom) / dt : 0;
    dofPulse = Math.max(0, dofPulse + zoomRate * 1.4 * dt - dofPulse * DOF_DECAY * dt);

    publish();

    if (isSettled()) {
      frame = null;
      return;
    }

    frame = window.requestAnimationFrame(step);
  }

  function wake() {
    if (destroyed || frame !== null) return;
    lastTime = performance.now();
    frame = window.requestAnimationFrame(step);
  }

  publish();
  // A camera that starts settled still owes its ready signal.
  if (phase === 'live') onIntroEnd?.();
  wake();

  return {
    /** Relative zoom step; positive pushes the camera in. */
    zoomBy(delta) {
      zoomTarget = clamp(zoomTarget * Math.exp(delta), ZOOM.min, ZOOM.max);
      wake();
    },

    resetZoom() {
      zoomTarget = 1;
      wake();
    },

    /**
     * StPageFlip renders a lone cover in the right half of the spread area and
     * a trailing lone page in the left half, so the camera slides sideways to
     * keep whatever is actually visible centred in frame.
     *
     * @param {number} offset - in the book's unscaled pixels
     */
    setFrameOffset(offset) {
      offsetXTarget = offset;
      wake();
    },

    setTier(nextTier) {
      currentTier = nextTier;
      intro = INTRO[currentTier];
      if (phase === 'intro') introElapsed = intro.duration;
      wake();
    },

    isIntroDone() {
      return phase === 'live';
    },

    destroy() {
      destroyed = true;
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = null;
    }
  };
}
